import { Drawer, Spin } from 'antd';
import { DateInput, Iteration, Parameter } from '@/pages/InteractiveSimulation/types/replayInteractive';
import { PlayerChartConfig, PlayerChartProvider } from '@/components/General/PlayerChart';
import dayjs from 'dayjs';
import { useState, useMemo } from 'react';
import InteractiveChartHeader from './InteractiveChartHeader';
import InteractiveEmptyState from './InteractiveEmptyState';
import { PlayerChart, TimelinePlayer } from '@/components/General/PlayerChart/components';
import { useChartDataManager, useInteractiveSimulationEvents, useHistoricalDataFetcher } from '@/hooks/InteractiveSimulation';

interface InfrastructureEvent {
	infrastructureId: number;
	infraName: string;
	mod_value: number;
}

interface TimeEvent {
	time: string;
	events: InfrastructureEvent[];
}

interface InteractiveSimulationChartPlayerProps {
	title: string;
	chartStartDate: DateInput;
	chartEndDate: DateInput;
	parameters: Parameter[];
	iterations: Iteration[];
	currentIteration: Iteration | null;
	isWaitingResponse: boolean;
	currentIterationCompleted: boolean;
	currentTime: DateInput;
	onCreateNewIteration: () => Promise<void>;
	isLivePlaying?: boolean;
	onLivePlay?: () => void;
	onLivePause?: () => void;
	events: TimeEvent[];
}

function InteractiveSimulationChartPlayer(props: InteractiveSimulationChartPlayerProps) {
	const instanceId = 'interactive-chart-player';

	const [switchEnabled, setSwitchEnabled] = useState(false);
	
	const { chartData, updateChartData, clearChartData, getChartDataMap } = useChartDataManager(props.parameters);

	const { historicalData, isLoadingHistoricalData, fetchHistoricalData, clearHistoricalData } = 
		useHistoricalDataFetcher({
			parameters: props.parameters,
			chartStartDate: props.chartStartDate,
			chartEndDate: props.chartEndDate,
		});

	const requestEvents = useMemo(() => {
		return props.events.map(timeEvent => ({
			timestamp: dayjs(timeEvent.time).valueOf(),
			timeEvent: timeEvent.time,
			requestData: timeEvent.events.map(event => ({
				infrastructureId: event.infrastructureId,
				mod_value: event.mod_value,
				nameInfrastructure: event.infraName,
			}))
		}));
	}, [props.events]);

	useInteractiveSimulationEvents({
		parameters: props.parameters,
		getChartDataMap,
		onDataReceived: updateChartData,
		onReset: clearChartData,
		onIterationChange: () => {},
	});

	const handleSwitchChange = (checked: boolean) => {
		setSwitchEnabled(checked);

		if (checked) {
			void fetchHistoricalData();
		} else {
			clearHistoricalData();
		}
	};

	const startDate = dayjs(props.chartStartDate).isValid()
		? dayjs(props.chartStartDate).toDate()
		: null;
	const endDate = dayjs(props.chartEndDate).isValid() ? dayjs(props.chartEndDate).toDate() : null;

	if (!startDate || !endDate) {
		return null;
	}

	const currentTimestamp = props.currentTime 
		? dayjs(props.currentTime).valueOf()
		: null;

	const playerChartConfig: PlayerChartConfig = {
		chartData: chartData,
		historicalData: historicalData,
		dateRange: {
			startDate: startDate,
			endDate: endDate,
		},
		liveMode: true,
		externalCurrentTime: currentTimestamp,
		requestEvents: requestEvents,
	};

	if (isLoadingHistoricalData) {
		console.log('Loading historical data...');
	}

	return (
		<PlayerChartProvider config={playerChartConfig} instanceId={instanceId}>
			<Drawer
				title={
					<div style={{ position: 'relative' }}>
						<InteractiveChartHeader
							mode="live"
							title={props.title}
							parameters={props.parameters}
							iterations={props.iterations}
							currentIteration={props.currentIteration}
							switchEnabled={switchEnabled}
							onSwitchChange={handleSwitchChange}
							isWaitingResponse={props.isWaitingResponse}
							currentIterationCompleted={props.currentIterationCompleted}
							onCreateNewIteration={props.onCreateNewIteration}
							isLivePlaying={props.isLivePlaying}
							onLivePlay={props.onLivePlay}
							onLivePause={props.onLivePause}
						/>
						{switchEnabled && isLoadingHistoricalData && (
							<div
								style={{
									position: 'absolute',
									top: 0,
									left: 0,
									right: 0,
									bottom: 0,
									backgroundColor: 'rgba(255, 255, 255, 0.7)',
									zIndex: 1002,
									pointerEvents: 'all',
								}}
							/>
						)}
					</div>
				}
				placement="bottom"
				onClose={() => {}}
				open={true}
				height="auto"
				styles={{ 
					body: { padding: 0, overflow: 'visible' },
					wrapper: { overflow: 'visible' }
				}}
				maskClosable={false}
				closable={false}
				mask={false}
				zIndex={1000}
			>
				<Spin
					spinning={switchEnabled && isLoadingHistoricalData}
					tip="Loading Historical Data..."
					className="w-full min-h-[200px]"
					style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1001 }}
				>
					<div className="w-full flex flex-col p-4">
						<div className="w-full pt-2" style={{ overflow: 'visible' }}>
							{props.parameters?.length === 0 ? (
								<InteractiveEmptyState />
							) : (
								<>
									<TimelinePlayer />
									<PlayerChart highChartHeight={160} />
								</>
							)}
						</div>
					</div>
				</Spin>
			</Drawer>
		</PlayerChartProvider>
	);
}

export default InteractiveSimulationChartPlayer;
